import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { notificationsAPI, leadsAPI, matrimonyAPI, settingsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

// CRM group's Leads entry is a placeholder here -- replaced at render time
// with one link per active Lead Category (Leads/Archive/Customer/...), see
// `navItems` in the component body. Kept as a single fallback item so the
// group never renders empty while categories are still loading.
const BASE_NAV_ITEMS = [
  { label: 'Dashboard', icon: 'bi-speedometer2', module: null,
    children: [
      { path: '/dashboard',           icon: 'bi-graph-up', label: 'CRM Dashboard',      module: 'DASHBOARD' },
      { path: '/matrimony/dashboard', icon: 'bi-heart',    label: 'Matrimony Dashboard', module: null },
    ]
  },
  { label: 'CRM', icon: 'bi-briefcase-fill', module: null,
    children: [
      { path: '/leads',              icon: 'bi-funnel',                label: 'Leads',       module: 'LEADS', isLeadCategorySlot: true },
      // #891: DailyQueueController/Repository (paged call queue, per-lead
      // call history, log-call action) was fully built server-side with a
      // ready API client (dailyQueueAPI) but no page and no nav entry --
      // same "built but undiscoverable" shape #848 fixed for Documents.
      { path: '/leads/daily-queue', icon: 'bi-telephone-outbound',       label: 'Daily Call Queue', module: 'LEADS' },
      { path: '/call-logs',         icon: 'bi-telephone',             label: 'Call Logs',   module: 'CALL_RECORDINGS' },
      // #856: Lead Statuses/Priorities/Sources moved into the generic
      // Masterdata screen (now its own top-level nav item below), so the
      // standalone Lead Settings page/link is retired -- see MasterdataHome's
      // leadstatus/leadpriority/leadsource entries.
      // #848: fully built, routed, and permission-gated (DOCUMENTS module
      // in the Roles & Permissions seed data) but had no sidebar link
      // anywhere -- completely undiscoverable except by typing the URL.
      { path: '/documents',          icon: 'bi-file-earmark-arrow-up', label: 'Documents',   module: 'DOCUMENTS' },
    ]
  },
  // #856: Masterdata moved out of the Matrimony group and promoted to its
  // own top-level item -- it's used site-wide (CRM lead config now lives
  // here too), not Matrimony-specific. No `children` -- renders as a direct
  // link instead of an expandable group.
  { path: '/matrimony/masterdata', icon: 'bi-database', label: 'Masterdata', module: null },
  { label: 'Matrimony', icon: 'bi-heart-pulse-fill', module: null,
    children: [
      { path: '/matrimony/profiles',       icon: 'bi-people',     label: 'Profiles',       module: null },
      { path: '/matrimony/archive',        icon: 'bi-archive',    label: 'Archive',        module: null },
      { path: '/matrimony/plans',          icon: 'bi-award',      label: 'Plans',          module: null },
      { path: '/matrimony/payments',        icon: 'bi-credit-card', label: 'Payments',        module: null },
      { path: '/matrimony/refund-requests', icon: 'bi-arrow-counterclockwise', label: 'Refund Requests', module: null },
    ]
  },
  { label: 'Approval', icon: 'bi-check2-square', module: null,
    children: [
      { path: '/matrimony/photo-approval',    icon: 'bi-image-fill',        label: 'Photo Approval',    module: null },
      { path: '/matrimony/jathagam-approval', icon: 'bi-file-earmark-text', label: 'Jathagam Approval', module: null },
      { path: '/matrimony/reported-profiles', icon: 'bi-flag-fill',         label: 'Reported Profiles', module: null },
    ]
  },
  { label: 'Communication', icon: 'bi-chat-left-dots-fill', module: null,
    children: [
      { path: '/communication/whatsapp-log', icon: 'bi-whatsapp',       label: 'WhatsApp Log (OTP/System)', module: null },
      { path: '/communication/email-log',    icon: 'bi-envelope-fill', label: 'Email Log',    module: null },
      // #930: private member-to-member conversation content -- gated on the
      // new MATRIMONY_MESSAGES module (CanView restricted to SUPER_ADMIN/
      // ADMIN/MANAGER) instead of module: null.
      { path: '/messages',                   icon: 'bi-chat-dots-fill', label: 'Messages',     module: 'MATRIMONY_MESSAGES' },
    ]
  },
  // #846: Templates/Bulk Send/its Message Log/Bulk History all had working
  // routes but zero sidebar entries -- only reachable by typing the exact
  // URL. Kept as its own section (not folded into Communication) so it
  // stays visually distinct from "WhatsApp Log (OTP/System)" above, per
  // #831's disambiguation -- this is the separate Business API
  // template/campaign system, not the OTP log.
  { label: 'WhatsApp Business', icon: 'bi-whatsapp', module: null,
    children: [
      { path: '/whatsapp/templates',    icon: 'bi-file-earmark-text', label: 'Templates',    module: null },
      { path: '/whatsapp/bulk-send',    icon: 'bi-send-fill',         label: 'Bulk Send',     module: null },
      { path: '/whatsapp/message-log',  icon: 'bi-list-check',        label: 'Message Log',   module: null },
      { path: '/whatsapp/bulk-history', icon: 'bi-clock-history',     label: 'Bulk History',  module: null },
    ]
  },
  { label: 'Administration', icon: 'bi-shield-lock-fill', module: null, adminOnly: true,
    children: [
      { path: '/users',      icon: 'bi-person-badge',   label: 'Users',         module: 'USERS',      adminOnly: true },
      { path: '/roles',      icon: 'bi-shield-lock',    label: 'Roles & Perms', module: 'ROLES',      adminOnly: true },
      // Gate on adminOnly only — the API already restricts /api/activities to
      // SUPER_ADMIN,ADMIN, and the granular ACTIVITIES permission row is absent
      // for roles seeded before that module existed (see migrations/
      // 2026-07-13_BackfillRolePermissions.sql), which used to hide this item.
      { path: '/activities', icon: 'bi-clipboard-data', label: 'Activity Log',  module: null,         adminOnly: true },
    ]
  },
  { label: 'Reports', icon: 'bi-bar-chart-line-fill', module: null,
    children: [
      { path: '/reports/leads',        icon: 'bi-funnel',            label: 'Lead Report',       module: null },
      { path: '/reports/conversion',   icon: 'bi-graph-up-arrow',    label: 'Conversion Report', module: null },
      { path: '/reports/performance',  icon: 'bi-person-lines-fill', label: 'User Performance',  module: null },
      { path: '/reports/followup',     icon: 'bi-calendar-check',    label: 'Followup Report',   module: null },
      { path: '/reports/activity',     icon: 'bi-activity',          label: 'Activity Report',   module: null },
      { path: '/reports/customer',     icon: 'bi-people',            label: 'Customer Report',   module: null },
      { path: '/reports/active-users', icon: 'bi-person-hearts',     label: 'Most Active Users', module: null },
      { path: '/reports/referral',     icon: 'bi-people-fill',       label: 'Referral Performance', module: null },
      { path: '/reports/refunds',      icon: 'bi-receipt-cutoff',    label: 'Refund / Cancellation', module: null },
      { path: '/reports/api-logs',     icon: 'bi-hdd-network',       label: 'API Logs',          module: null },
      { path: '/reports/fraud-watch',  icon: 'bi-shield-exclamation', label: 'Fraud Watch',      module: null },
      { path: '/incentives',           icon: 'bi-cash-coin',         label: 'Employee Incentives', module: null },
      { path: '/communication/whatsapp-log', icon: 'bi-whatsapp',    label: 'WhatsApp Report (OTP/System)', module: null },
      { path: '/communication/email-log',    icon: 'bi-envelope-fill', label: 'Email Report',    module: null },
    ]
  },
  // App content — managed by admins; gated on adminOnly only (no per-module seed).
  { label: 'App Content', icon: 'bi-window-sidebar', module: null, adminOnly: true,
    children: [
      { path: '/content/faq',              icon: 'bi-question-circle', label: 'FAQ',              module: null, adminOnly: true },
      { path: '/content/success-stories',  icon: 'bi-heart',           label: 'Success Stories',  module: null, adminOnly: true },
      { path: '/content/support-settings', icon: 'bi-headset',         label: 'Support Settings', module: null, adminOnly: true },
    ]
  },
  // #280: staff had no dedicated place to check member-submitted Contact Us /
  // Feedback messages or delete-profile requests -- grouped under their own
  // menu (moved out of App Content and Matrimony respectively) so they're
  // easy to find, per direct request on the ticket. Same routes/pages as
  // before, only the nav grouping changed.
  { label: 'Support', icon: 'bi-life-preserver', module: null,
    children: [
      { path: '/content/contact',           icon: 'bi-envelope',      label: 'Contact Us',              module: null, adminOnly: true },
      { path: '/content/feedback',          icon: 'bi-chat-heart',    label: 'Feedback',                module: null, adminOnly: true },
      { path: '/matrimony/delete-requests', icon: 'bi-person-dash',   label: 'Delete Profile Requests',  module: null },
      { path: '/matrimony/zero-access-log', icon: 'bi-exclamation-diamond', label: 'Zero Access Log',   module: null },
    ]
  },
  { label: 'Help', icon: 'bi-question-circle-fill', module: null,
    children: [
      { path: '/help',                     icon: 'bi-life-preserver',   label: 'Help Home',          module: null },
      { path: '/help/employee-incentive',  icon: 'bi-cash-coin',        label: 'Employee Incentive', module: null },
      { path: '/help/plan-details',        icon: 'bi-award',            label: 'Plan Details',       module: null },
    ]
  },
];

export default function AppLayout() {
  const { user, logout, hasPermission, isAdmin, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expanded, setExpanded] = useState({});
  const lastSeenIdRef = useRef(null);
  const [leadCategories, setLeadCategories] = useState([]);

  // Global search
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);

  // CRM sidebar's Leads entry is replaced by one link per active Lead
  // Category (Leads/Archive/Customer/...), fetched once. Falls back to the
  // static "Leads" placeholder (all leads, unfiltered) while loading/on error
  // so the group is never empty.
  useEffect(() => {
    settingsAPI.getLeadConfig('categories').then(res => {
      if (res.data.Success) {
        setLeadCategories((res.data.Data || [])
          .filter(c => c.IsActive)
          .sort((a, b) => a.DisplayOrder - b.DisplayOrder));
      }
    }).catch(() => {});
  }, []);

  const navItems = useMemo(() => BASE_NAV_ITEMS.map(item => {
    if (!item.children) return item;
    return {
      ...item,
      children: item.children.flatMap(child => {
        if (!child.isLeadCategorySlot) return [child];
        if (leadCategories.length === 0) return [child];
        return leadCategories.map(c => ({
          path: `/leads?category=${c.ID}`,
          icon: child.icon,
          label: c.Name,
          module: child.module,
        }));
      }),
    };
  }), [leadCategories]);

  // Nav-item paths that carry a query string (the per-category Leads links)
  // need the query compared too -- otherwise every category link would match
  // the same /leads pathname and all show "active" at once.
  const isChildPathActive = (childPath) => {
    if (!childPath.includes('?')) return null; // let NavLink's own isActive decide
    return (location.pathname + location.search) === childPath;
  };
  const childBasePath = (childPath) => childPath.split('?')[0];

  // Auto-expand parent nav items whose children match the current path
  useEffect(() => {
    navItems.forEach(item => {
      if (item.children) {
        const active = item.children.some(c => location.pathname.startsWith(childBasePath(c.path)));
        if (active) setExpanded(prev => ({ ...prev, [item.label]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navItems]);

  const toggleExpand = (label) =>
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchRes([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced global search
  const runSearch = useCallback(async (q) => {
    if (!q.trim() || q.length < 2) { setSearchRes([]); return; }
    setSearchLoading(true);
    try {
      const [leadsRes, matrimonyRes] = await Promise.allSettled([
        leadsAPI.getAll({ SearchTerm: q, PageSize: 4, PageNumber: 1 }),
        matrimonyAPI.getAll({ search: q, size: 3, page: 1 }),
      ]);
      const leads = leadsRes.status === 'fulfilled' && leadsRes.value.data.Success
        ? (leadsRes.value.data.Data || []).map(l => ({ type: 'lead', id: l.LeadID, label: [l.FirstName, l.LastName].filter(Boolean).join(' '), sub: l.Mobile || l.Email || '', path: `/leads/${l.LeadID}` }))
        : [];
      const profiles = matrimonyRes.status === 'fulfilled' && matrimonyRes.value.data.Success
        ? (matrimonyRes.value.data.Data || []).map(p => ({ type: 'profile', id: p.ProfileID, label: [p.FirstName, p.LastName].filter(Boolean).join(' '), sub: p.MobileNo || p.ProfileUniqueID || '', path: `/matrimony/profiles/${p.ProfileID}` }))
        : [];
      setSearchRes([...leads, ...profiles]);
    } catch {}
    setSearchLoading(false);
  }, []);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchRes([]); return; }
    const t = setTimeout(() => runSearch(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  // Separate mobile (≤768px) and desktop toggle behaviour
  const handleToggle = () => {
    if (window.innerWidth <= 768) {
      setMobileOpen(prev => !prev);
    } else {
      setCollapsed(prev => !prev);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getAll({ isRead: false, page: 1, size: 5 });
      if (res.data.Success) {
        const data = res.data.Data;
        setUnreadCount(data?.UnreadCount || 0);
        const latest = data?.Notifications || [];
        if (latest.length > 0) {
          const maxId = Math.max(...latest.map(n => n.NotificationID));
          if (lastSeenIdRef.current !== null) {
            const newOnes = latest.filter(n => n.NotificationID > lastSeenIdRef.current);
            newOnes.sort((a, b) => a.NotificationID - b.NotificationID).forEach(n => {
              toast.info(
                <div onClick={() => navigate('/notifications')} style={{ cursor: 'pointer' }}>
                  <div className="fw-semibold" style={{ fontSize: 13 }}>{n.Title}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{n.Message}</div>
                </div>,
                { icon: <i className="bi bi-bell-fill text-primary" /> }
              );
            });
          }
          lastSeenIdRef.current = maxId;
        }
      }
    } catch {}
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Logout?',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, logout'
    });
    if (result.isConfirmed) {
      await logout();
      navigate('/login');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isItemVisible = (item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (!item.module) return true;
    return hasPermission(item.module, 'CanView');
  };

  const visibleNav = navItems
    .filter(isItemVisible)
    .map(item => item.children
      ? { ...item, children: item.children.filter(isItemVisible) }
      : item
    )
    .filter(item => !item.children || item.children.length > 0);

  return (
    <div className="app-layout">
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
          style={{ zIndex: 1039 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          {collapsed
            ? <img src="/logo.png" alt="Logo" style={{ width: 42, height: 42, objectFit: 'contain', borderRadius: 6 }} />
            : <img src="/logo.png" alt="Melathalam Matrimony CRM" style={{ height: 65, maxWidth: 200, objectFit: 'contain' }} />
          }
        </div>

        <div className="sidebar-nav">
          {visibleNav.map(item => (
            <div key={item.label}>
              {item.children ? (
                <>
                  {/* ── Expandable parent ── */}
                  <button
                    className={`sidebar-link w-100 border-0 bg-transparent text-start ${
                      item.children.some(c => location.pathname.startsWith(childBasePath(c.path))) ? 'active' : ''
                    }`}
                    style={{ cursor: 'pointer' }}
                    title={collapsed ? item.label : ''}
                    onClick={() => toggleExpand(item.label)}
                  >
                    <i className={`bi ${item.icon} nav-icon`} />
                    {!collapsed && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        <i className={`bi bi-chevron-${expanded[item.label] ? 'up' : 'down'} ms-auto`}
                          style={{ fontSize: 11, opacity: 0.7 }} />
                      </>
                    )}
                  </button>
                  {(expanded[item.label] || collapsed) && item.children.map(child => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      className={({ isActive }) => {
                        const active = isChildPathActive(child.path) ?? isActive;
                        return `sidebar-link ${active ? 'active' : ''} ${!collapsed ? 'ps-4' : ''}`;
                      }}
                      title={collapsed ? child.label : ''}
                    >
                      <i className={`bi ${child.icon} nav-icon`} style={{ fontSize: 13 }} />
                      {!collapsed && <span className="nav-label" style={{ fontSize: 13 }}>{child.label}</span>}
                    </NavLink>
                  ))}
                </>
              ) : (
                /* ── Standalone top-level item (no group/children) ── */
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  title={collapsed ? item.label : ''}
                >
                  <i className={`bi ${item.icon} nav-icon`} />
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                </NavLink>
              )}
            </div>
          ))}
        </div>

        {/* User Info */}
        <div className="sidebar-user p-3 border-top" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="d-flex align-items-center gap-2">
            <div className="avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
              {user?.ProfilePicture
                ? <img src={user.ProfilePicture} alt="" className="w-100 h-100 rounded-circle object-fit-cover" />
                : getInitials(user?.FullName)
              }
            </div>
            {!collapsed && (
              <div className="flex-1 overflow-hidden">
                <div className="text-white fw-semibold text-truncate" style={{ fontSize: 12 }}>
                  {user?.FullName}
                </div>
                <div className="text-truncate" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                  {user?.RoleName}
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className={`main-content ${collapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Topbar */}
        <header className="topbar">
          <button className="topbar-toggle" onClick={handleToggle}>
            <i className="bi bi-list" />
          </button>

          {/* Global Search */}
          <div className="topbar-search d-none d-md-block" ref={searchRef} style={{ position: 'relative' }}>
            <div className="input-group input-group-sm">
              <span className="input-group-text border-0 bg-transparent">
                {searchLoading
                  ? <span className="spinner-border spinner-border-sm text-muted" style={{ width: 14, height: 14 }} />
                  : <i className="bi bi-search text-muted" />}
              </span>
              <input
                type="text"
                className="form-control border-0"
                placeholder="Search leads, profiles…"
                style={{ background: '#f8fafc' }}
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchQ.trim()) {
                    navigate(`/leads?search=${encodeURIComponent(searchQ.trim())}`);
                    setSearchQ(''); setSearchRes([]);
                  }
                  if (e.key === 'Escape') { setSearchQ(''); setSearchRes([]); }
                }}
              />
              {searchQ && (
                <button className="btn btn-sm border-0 bg-transparent" onClick={() => { setSearchQ(''); setSearchRes([]); }}>
                  <i className="bi bi-x text-muted" />
                </button>
              )}
            </div>

            {/* Results dropdown */}
            {(searchRes.length > 0 || (searchLoading && searchQ.length >= 2)) && (
              <div className="position-absolute bg-white shadow border rounded-3 mt-1 py-1"
                style={{ zIndex: 9999, top: '100%', left: 0, right: 0, minWidth: 320 }}>
                {searchLoading && searchRes.length === 0 && (
                  <div className="px-3 py-2 text-muted small">Searching…</div>
                )}
                {searchRes.map(item => (
                  <button key={`${item.type}-${item.id}`}
                    className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                    onClick={() => { navigate(item.path); setSearchQ(''); setSearchRes([]); }}>
                    <i className={`bi ${item.type === 'lead' ? 'bi-person-circle text-primary' : 'bi-heart-fill text-danger'}`}
                      style={{ fontSize: 15, flexShrink: 0 }} />
                    <div className="overflow-hidden">
                      <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>{item.label || '—'}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                        {item.type === 'lead' ? 'Lead' : 'Matrimony'} • {item.sub}
                      </div>
                    </div>
                  </button>
                ))}
                {searchRes.length > 0 && (
                  <div className="border-top px-3 py-1">
                    <button className="btn btn-link btn-sm p-0 text-muted" style={{ fontSize: 11 }}
                      onClick={() => { navigate(`/leads?search=${encodeURIComponent(searchQ)}`); setSearchQ(''); setSearchRes([]); }}>
                      See all matching leads →
                    </button>
                  </div>
                )}
                {!searchLoading && searchRes.length === 0 && (
                  <div className="px-3 py-2 text-muted small">No results found</div>
                )}
              </div>
            )}
          </div>

          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
              <i className={`bi bi-${theme === 'light' ? 'moon' : 'sun'}`} />
            </button>

            <button
              className="topbar-btn"
              onClick={() => navigate('/notifications')}
              title="Notifications"
            >
              <i className="bi bi-bell" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>

            <div className="dropdown">
              <button
                className="topbar-btn dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                  {getInitials(user?.FullName)}
                </div>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow">
                <li>
                  <div className="dropdown-item-text px-3 py-2">
                    <div className="fw-semibold">{user?.FullName}</div>
                    <small className="text-muted">{user?.Email}</small>
                  </div>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item" onClick={() => navigate('/profile')}>
                    <i className="bi bi-person me-2" />My Profile
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => navigate('/settings')}>
                    <i className="bi bi-gear me-2" />Settings
                  </button>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2" />Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="content-area fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
